import { Copy, ExternalLink, GalleryHorizontalEnd, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ClientVaultEventSelector } from "../../components/ClientVaultEventSelector";
import { PageHeader } from "../../components/PageHeader";
import { normalizeBaseUrl } from "../../lib/format";
import { useClientVaultContext } from "../../lib/useClientVaultContext";
import { useConfig } from "../../lib/useConfig";

const invitationMessage =
  "We would love to collect your photos and videos from the event. Please upload your favorite memories using this link. All uploads will be reviewed before appearing in the Memory Hub.";

export function UploadLinkPage() {
  const config = useConfig();
  const vaultContext = useClientVaultContext();

  const base = normalizeBaseUrl(config?.publicBaseUrl || window.location.origin);
  const selectedEvent = vaultContext.selectedVault?.event;
  const absoluteUrl = (value: string) => (value.startsWith("http") ? value : `${base}${value}`);
  const uploadUrl = selectedEvent ? absoluteUrl(selectedEvent.uploadUrl) : "";
  const galleryUrl = selectedEvent ? absoluteUrl(selectedEvent.galleryUrl) : "";
  const hubUrl = selectedEvent?.memoryHubUrl ? absoluteUrl(selectedEvent.memoryHubUrl) : "";

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value);
  };

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Guest Upload Link"
        title="Share one simple upload link with guests."
        subtitle="Guests can submit photos and videos without creating an account. All uploads go into pending review first."
      />
      <ClientVaultEventSelector
        context={vaultContext.context}
        selectedVault={vaultContext.selectedVault}
        selectedVaultId={vaultContext.selectedVaultId}
        loading={vaultContext.loading}
        error={vaultContext.error}
        onChange={vaultContext.selectVault}
      />
      {!selectedEvent ? (
        <section className="ivory-card rounded-lg p-8 text-sm font-bold text-ktd-muted">
          Create or assign an event vault before sharing a guest upload link.
        </section>
      ) : (
        <>
      <section className="grid gap-6 lg:grid-cols-[1fr_.8fr]">
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <Link2 className="size-8 text-[#D94A16]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Guest Upload URL</h2>
          <div className="mt-5 rounded-lg border border-ktd-gold/25 bg-white p-4 text-sm font-bold text-ktd-rich">
            {uploadUrl}
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => void copy(uploadUrl)}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold"
            >
              <Copy className="size-4" />
              Copy Upload Link
            </button>
            <a
              href={uploadUrl}
              target="_blank"
              rel="noreferrer"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-full border border-ktd-gold/35 px-5 py-3 text-sm font-extrabold text-ktd-rich"
            >
              <ExternalLink className="size-4" />
              Open Upload Page
            </a>
          </div>
        </article>
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <GalleryHorizontalEnd className="size-8 text-[#D94A16]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Memory Hub Links</h2>
          <div className="mt-5 grid gap-3">
            <LinkBox label="Approved Gallery" value={galleryUrl} />
            <LinkBox label="Featured Memories" value={hubUrl || "Memory Hub link not set"} />
          </div>
        </article>
      </section>
      <section className="ivory-card rounded-lg p-6 sm:p-8">
        <h2 className="font-display text-3xl font-bold">Prewritten Guest Message</h2>
        <p className="mt-4 rounded-lg bg-white p-5 text-lg leading-8 text-ktd-rich">{invitationMessage}</p>
        <button
          type="button"
          onClick={() => void copy(`${invitationMessage}\n\n${uploadUrl}`)}
          className="focus-ring mt-5 inline-flex items-center gap-2 rounded-full bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black"
        >
          <Copy className="size-4" />
          Copy Message
        </button>
      </section>
      <Link
        to="/client/pending"
        className="focus-ring inline-flex justify-center rounded-lg bg-ktd-black px-5 py-4 text-sm font-extrabold text-ktd-softGold"
      >
        Review Guest Uploads
      </Link>
        </>
      )}
    </div>
  );
}

function LinkBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-white p-4">
      <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#D94A16]">{label}</p>
      <p className="mt-2 break-all text-sm font-bold text-ktd-rich">{value}</p>
    </div>
  );
}
